import { MouseEvent, useEffect, useId, useState } from 'react';
import styled from '@emotion/styled';
import appStoreBadgeUrl from '@/assets/badges/download-on-the-app-store.svg?url';
import { apiStore } from '@/stores/ApiStore';
import { isInWebView } from '@/hooks/useWebViewMessaging';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import {
  buildAppsFlyerDeeplink,
  buildAppsFlyerRedirectUrl,
} from '@/utils/deeplink';

const Card = styled.div`
  display: grid;
  justify-items: center;
  gap: 8px;
  margin-top: 18px;
  padding: 14px 12px 12px;
  border: 1px solid rgba(147, 73, 164, 0.14);
  border-radius: 14px;
  background: linear-gradient(135deg, rgba(84, 127, 255, 0.04), rgba(252, 79, 246, 0.05));
  text-align: center;
`;

const Description = styled.p`
  margin: 0;
  color: #6b7280;
  font-size: 12px;
  line-height: 16px;
`;

const BadgeLink = styled.a<{ $processing: boolean }>`
  display: inline-flex;
  border-radius: 9px;
  cursor: ${({ $processing }) => ($processing ? 'progress' : 'pointer')};
  transition: transform 0.2s ease;
  -webkit-tap-highlight-color: transparent;

  &:hover {
    transform: ${({ $processing }) => ($processing ? 'none' : 'translateY(-1px)')};
  }

  &:focus-visible {
    outline: 3px solid #9349a4;
    outline-offset: 3px;
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

const BadgeContent = styled.div`
  display: grid;
  width: 150px;
  height: 50px;
  place-items: center;
`;

const BadgeImage = styled.img`
  display: block;
  width: 150px;
  height: 50px;
`;

interface AppStoreDownloadBadgeProps {
  description?: string;
}

function getRedirectUrl(handoffToken?: string): string {
  const deeplink = buildAppsFlyerDeeplink({ handoffToken });

  return buildAppsFlyerRedirectUrl(
    null,
    deeplink,
    'app_store_badge',
  );
}

function waitForLoadingPaint(): Promise<void> {
  return new Promise((resolve) => {
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => resolve());
    });
  });
}

export function AppStoreDownloadBadge({
  description = 'Get Shieldix for iPhone',
}: AppStoreDownloadBadgeProps) {
  const [processing, setProcessing] = useState(false);
  const descriptionId = useId();

  useEffect(() => {
    const resetProcessing = () => setProcessing(false);
    window.addEventListener('pageshow', resetProcessing);

    return () => window.removeEventListener('pageshow', resetProcessing);
  }, []);

  if (isInWebView()) {
    return null;
  }

  const fallbackHref = getRedirectUrl();

  const handleClick = async (event: MouseEvent<HTMLAnchorElement>) => {
    if (
      event.button !== 0
      || event.metaKey
      || event.ctrlKey
      || event.shiftKey
      || event.altKey
    ) {
      return;
    }

    event.preventDefault();
    if (processing) {
      return;
    }

    setProcessing(true);

    try {
      await waitForLoadingPaint();

      let handoffToken: string | undefined;
      if (apiStore.isAuthenticated) {
        const handoff = await apiStore.createHandoff();
        handoffToken = handoff.ok ? handoff.token : undefined;
      }

      const redirectUrl = getRedirectUrl(handoffToken);
      window.location.assign(redirectUrl);
    }
    catch {
      setProcessing(false);
    }
  };

  return (
    <Card>
      <Description id={descriptionId}>{description}</Description>
      <BadgeLink
        href={fallbackHref}
        onClick={handleClick}
        $processing={processing}
        aria-describedby={descriptionId}
        aria-busy={processing}
        aria-disabled={processing}
        aria-label={processing
          ? 'Opening Shieldix in the App Store'
          : 'Download Shieldix on the App Store'}
      >
        <BadgeContent>
          {processing
            ? <LoadingSpinner size="small" minHeight="50px" />
            : (
                <BadgeImage
                  src={appStoreBadgeUrl}
                  width={150}
                  height={50}
                  alt=""
                />
              )}
        </BadgeContent>
      </BadgeLink>
    </Card>
  );
}
